import React from 'react';
import { Alert, Button, Stack, Tab, Tabs } from '@mui/material';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';

import BudgetConsolidationCardsPanel from './BudgetConsolidationCardsPanel';
import BudgetControlSummaryPanel from './BudgetControlSummaryPanel';
import BudgetConsolidationAccountGrid from './BudgetConsolidationAccountGrid';
import BudgetConsolidationCostCenterGrid from './BudgetConsolidationCostCenterGrid';
import BudgetArbitrationGrid from './BudgetArbitrationGrid';
import { BudgetConsolidationWorkbenchModel } from './budgetconsolidation.models';
import {
  fetchBudgetArbitrations,
  fetchBudgetConsolidationAccount,
  fetchBudgetConsolidationCards,
  fetchBudgetConsolidationCostCenter,
  fetchBudgetConsolidationWorkbench,
  fetchBudgetControlAreas,
  saveBudgetArbitrations,
  transitionBudgetConsolidation,
} from './budgetconsolidation.apis';
import { PlanningPageProps, PlanningWorkbenchShell } from '../planningcore';
import { fetchPlanningJourneyPhases } from '../core/planningJourney.cache';
import FPlanningJourney from '../core/FPlanningJourney';

interface State {
  model: BudgetConsolidationWorkbenchModel | null;
  tab: number;
  loading: boolean;
  saving: boolean;
  error: string;
  success: string;
  phases: any[];
}

export default class BudgetConsolidationWorkbench extends React.Component<PlanningPageProps, State> {
  state: State = {
    model: null,
    tab: 0,
    loading: false,
    saving: false,
    error: '',
    success: '',
    phases: [],
  };

  componentDidMount() { void this.initialize(); }

  private pci = () => Number(this.props.plancontext?.pci || 0);

  private initialize = async () => {
    const pci = this.pci();
    if (!pci) {
      this.setState({ error: 'Planning Cycle context is missing.' });
      return;
    }

    this.setState({ loading: true, error: '', success: '' });
    try {
      const phases = await fetchPlanningJourneyPhases();
      let model: BudgetConsolidationWorkbenchModel;

      try {
        model = await fetchBudgetConsolidationWorkbench(pci);
      } catch {
        const [cards, accounts, costcenters, controlareas, arbitrations] = await Promise.all([
          fetchBudgetConsolidationCards(pci),
          fetchBudgetConsolidationAccount(pci),
          fetchBudgetConsolidationCostCenter(pci),
          fetchBudgetControlAreas(pci).catch(() => []),
          fetchBudgetArbitrations(pci).catch(() => []),
        ]);
        model = {
          context: {
            planningcycleid: pci,
            planningcyclecode: this.props.plancontext?.pcc || '',
            planningcyclename: this.props.plancontext?.pcn || '',
            budgetperiodid: Number(this.props.plancontext?.bpi || 0),
            budgetperiodcode: this.props.plancontext?.bpc || '',
            budgetperiodname: this.props.plancontext?.bpn || '',
            phasecode: 'BUDGET CONSOLIDATION',
          },
          cards,
          accounts,
          costcenters,
          controlareas,
          arbitrations,
          actions: {
            canRefresh: true,
            canArbitrate: true,
            canSaveArbitration: true,
            canComplete: false,
            canReturn: true,
            canExit: false,
            nextpage: null,
          },
        };
      }

      this.setState({ model, phases });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private saveArbitration = async (records: any[]) => {
    this.setState({ saving: true, error: '', success: '' });
    try {
      await saveBudgetArbitrations(this.pci(), records);
      this.setState({ success: 'Arbitration decisions saved.' });
      await this.initialize();
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
      throw error;
    } finally {
      this.setState({ saving: false });
    }
  };

  private transition = async (action: 'COMPLETE' | 'RETURN') => {
    const model = this.state.model;
    if (!model) return;
    this.setState({ saving: true, error: '', success: '' });
    try {
      await transitionBudgetConsolidation(this.pci(), action, model.context.stamp);
      this.setState({ success: action === 'COMPLETE' ? 'Budget Consolidation completed.' : 'Budget Consolidation returned for correction.' });
      await this.initialize();
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ saving: false });
    }
  };

  render() {
    const { model } = this.state;
    const actions = model?.actions;

    return (
      <PlanningWorkbenchShell
        {...this.props}
        title={this.props.title || 'Budget Consolidation & Arbitration'}
        subtitle="Consolidate Activity, Direct and Investment budgets, compare them with Budget Control, and record governance arbitration decisions."
        icon={<AccountBalanceOutlinedIcon />}
        loading={this.state.loading}
        saving={this.state.saving}
        error={this.state.error}
        success={this.state.success}
        onRefresh={this.initialize}
        headerExtension={
          <FPlanningJourney
            phases={this.state.phases}
            currentPhaseCode={model?.context.phasecode || 'BUDGET CONSOLIDATION'}
          />
        }
        actions={
          <Stack direction="row" spacing={1}>
            {actions?.canReturn && (
              <Button variant="outlined" startIcon={<UndoOutlinedIcon />} onClick={() => this.transition('RETURN')}>
                Return for Correction
              </Button>
            )}
            {actions?.canComplete && (
              <Button variant="contained" startIcon={<DoneAllOutlinedIcon />} onClick={() => this.transition('COMPLETE')}>
                Complete Consolidation
              </Button>
            )}
          </Stack>
        }
      >
        {!model ? (
          <Alert severity="info">No Budget Consolidation context is available.</Alert>
        ) : (
          <Stack spacing={3}>
            <BudgetConsolidationCardsPanel cards={model.cards} />
            <BudgetControlSummaryPanel rows={model.controlareas} />

            {model.cards.unmappedlines > 0 && (
              <Alert severity="warning">
                {model.cards.unmappedlines} unmapped line(s), worth {Number(model.cards.unmappedamount || 0).toLocaleString('fr-FR')}, must be resolved before completion.
              </Alert>
            )}

            {model.cards.totalbudget > model.cards.approvedcapacity && (
              <Alert severity="warning">
                Consolidated demand exceeds approved capacity. Arbitration must reduce or reassign the excess before completion.
              </Alert>
            )}

            <Tabs value={this.state.tab} onChange={(_, tab) => this.setState({ tab })}>
              <Tab label="By Account" />
              <Tab label="By Cost Center" />
              <Tab label={`Arbitration (${model.cards.pendingarbitrations || 0})`} />
            </Tabs>

            {this.state.tab === 0 && (
              <BudgetConsolidationAccountGrid rows={model.accounts} loading={this.state.loading} />
            )}

            {this.state.tab === 1 && (
              <BudgetConsolidationCostCenterGrid rows={model.costcenters} loading={this.state.loading} />
            )}

            {this.state.tab === 2 && (
              <BudgetArbitrationGrid
                rows={model.arbitrations}
                loading={this.state.loading || this.state.saving}
                editable={actions?.canArbitrate}
                onRowsChange={arbitrations => this.setState({ model: { ...model, arbitrations } })}
                onSave={this.saveArbitration}
              />
            )}
          </Stack>
        )}
      </PlanningWorkbenchShell>
    );
  }
}
